import React, { useRef, useState } from 'react';
import {
  BetweenVerticalStart,
  ChevronDown,
  ChevronUp,
  Copy,
  FilePlus,
  Plus,
  RotateCw,
  RotateCcw,
  Trash2,
  Files,
} from 'lucide-react';
import type * as pdfjsLib from 'pdfjs-dist';
import { PageNavigator } from './PageNavigator';

interface PagesPanelProps {
  file: File | null;
  pageCount: number;
  liveThumbnails?: Record<number, string>;
  sharedPdfDoc?: pdfjsLib.PDFDocumentProxy | null;
  disabled?: boolean;
  onPageSelect?: (pageIndex: number) => void;
  /** Called when the user picks a PDF via the panel Merge control. */
  onMergeFileSelected?: (file: File) => void;
  /** Called with an optional afterIndex; undefined = insert after active page */
  onInsertBlank?: (afterIndex?: number) => void;
  onDuplicatePage?: (pageIndex: number) => void;
  onDeletePage?: (pageIndex: number) => void;
  onRotatePage?: (pageIndex: number, direction?: 'cw' | 'ccw') => void;
  activePageIndex?: number;
  /**
   * Called when the user drag-and-drops a page thumbnail to a new position.
   * order[newPosition] = oldIndex — the same convention as the server's reorder action.
   * Wire this to: pageOps.reorderPages(order)
   */
  onReorderPages?: (order: number[]) => void;
}

export const PagesPanel: React.FC<PagesPanelProps> = ({
  file,
  pageCount,
  liveThumbnails,
  sharedPdfDoc,
  disabled = false,
  onPageSelect,
  onMergeFileSelected,
  onInsertBlank,
  onDuplicatePage,
  onDeletePage,
  onRotatePage,
  activePageIndex = 0,
  onReorderPages,
}) => {
  const mergeInputRef = useRef<HTMLInputElement>(null);
  // Collapse thumbnails by default on narrow viewports so the filmstrip
  // and fixed zoom bar do not cover the PDF canvas tap targets.
  const [thumbsExpanded, setThumbsExpanded] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(min-width: 768px)').matches;
  });

  if (pageCount === 0) return null;

  return (
    <div
      className={`editor-pages-filmstrip${thumbsExpanded ? '' : ' editor-pages-filmstrip--collapsed'}`}
      aria-label="Pages and page tools"
    >
      <div className="editor-pages-filmstrip-tools">
        <div className="editor-pages-header">
          <div className="editor-pages-title">Pages</div>
          <button
            type="button"
            className="editor-pages-toggle"
            onClick={() => setThumbsExpanded((prev) => !prev)}
            aria-expanded={thumbsExpanded}
            aria-controls="editor-pages-thumbnails"
            aria-label={thumbsExpanded ? 'Collapse pages panel' : 'Expand pages panel'}
            title={thumbsExpanded ? 'Collapse thumbnails' : 'Expand thumbnails'}
          >
            {thumbsExpanded ? (
              <ChevronDown size={14} strokeWidth={2} aria-hidden />
            ) : (
              <ChevronUp size={14} strokeWidth={2} aria-hidden />
            )}
          </button>
        </div>
        <div className="editor-pages-meta" aria-live="polite">
          {activePageIndex + 1} / {pageCount}
        </div>
        <div className="editor-pages-actions">
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled || pageCount === 0 || !onMergeFileSelected}
            title="Add / merge PDF"
            aria-label="Merge another PDF into this document"
            onClick={() => mergeInputRef.current?.click()}
          >
            <Files size={12} strokeWidth={1.75} />
            <span>Merge</span>
          </button>
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Insert blank page before current page"
            aria-label="Insert blank page before current page"
            onClick={() => onInsertBlank?.(activePageIndex - 1)}
          >
            <BetweenVerticalStart size={12} strokeWidth={1.75} />
            <span>Before</span>
          </button>
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Insert blank page after current page"
            aria-label="Insert blank page after current page"
            onClick={() => onInsertBlank?.()}
          >
            <FilePlus size={12} strokeWidth={1.75} />
            <span>Blank</span>
          </button>
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Duplicate current page"
            aria-label="Duplicate current page"
            onClick={() => onDuplicatePage?.(activePageIndex)}
          >
            <Copy size={12} strokeWidth={1.75} />
            <span>Dup</span>
          </button>
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Rotate current page 90° clockwise"
            aria-label="Rotate current page 90° clockwise"
            onClick={() => onRotatePage?.(activePageIndex, 'cw')}
          >
            <RotateCw size={12} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Rotate current page 90° counter-clockwise"
            aria-label="Rotate current page 90° counter-clockwise"
            onClick={() => onRotatePage?.(activePageIndex, 'ccw')}
          >
            <RotateCcw size={12} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className="editor-pages-action editor-pages-action-danger"
            disabled={disabled || pageCount <= 1}
            title="Delete current page"
            aria-label="Delete current page"
            onClick={() => {
              if (pageCount <= 1) return;
              if (confirm(`Delete page ${activePageIndex + 1}? Fields on this page will be removed.`)) {
                onDeletePage?.(activePageIndex);
              }
            }}
          >
            <Trash2 size={12} strokeWidth={1.75} />
          </button>
          {/* BUG-08: second button explicitly inserts at the END (afterIndex = last page),
              distinct from the "Blank" button which inserts after the active page */}
          <button
            type="button"
            className="editor-pages-action"
            disabled={disabled}
            title="Add blank page at end of document"
            aria-label="Add blank page at end of document"
            onClick={() => onInsertBlank?.(pageCount - 1)}
          >
            <Plus size={12} strokeWidth={1.75} />
            <span>End</span>
          </button>
        </div>
      </div>

      {thumbsExpanded && (
        <div id="editor-pages-thumbnails" className="editor-pages-thumbnails">
          <PageNavigator
            variant="filmstrip"
            file={file}
            pageCount={pageCount}
            activePageIndex={activePageIndex}
            onPageSelect={(idx) => onPageSelect?.(idx)}
            liveThumbnails={liveThumbnails}
            sharedPdfDoc={sharedPdfDoc}
            onReorderPages={disabled ? undefined : onReorderPages}
          />
        </div>
      )}

      <input
        ref={mergeInputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0];
          e.target.value = '';
          if (picked) onMergeFileSelected?.(picked);
        }}
      />
    </div>
  );
};